import React, { FC, useState } from 'react'
import { Formik, FormikProps } from 'formik'
import ProductEditForm from './ProductEditForm'
import ProductSetForm from './ProductSetForm'
import { FormikHandleSubmit } from '@typings/Formik'
import { Product, ProductType } from '@typings/entities/Product'
import { ProductCode } from '@typings/entities/ProductCode'
import { Ability, withAcl } from '@components/blocks/Acl'
import { ExternalFormProps } from './types'
import { createValidationSchema } from './validation'
import omit from 'lodash/omit'

type Props = Omit<ExternalFormProps, 'type'> & {
  initialValues?: Product
  onSubmit: FormikHandleSubmit<Product>
  ability: Ability
  onDownloadAttachment: (id: string, fileName?: string) => Promise<void>
}

const initialValuesEmpty = {}

const ProductEditFormFormik: FC<Props> = ({ initialValues = initialValuesEmpty, onSubmit, ability, ...rest }) => {
  const [type, setType] = useState<ProductType>(initialValues.type || 'physical')

  const validateSku = rest.createView || ability.can('supervisorEdit', 'Product')
  const validationSchema = createValidationSchema(type, validateSku)

  const handleOnSubmit: FormikHandleSubmit<Product> = React.useCallback(
    (values, helpers) => {
      return onSubmit(
        {
          ...values,
          expirationOffset: values.expirationOffset || null,
          inboundExpirationOffset: values.inboundExpirationOffset || null,
          outboundExpirationOffset: values.outboundExpirationOffset || null,
          codes: values.codes?.filter((code) => !!code.value).map((code) => omit(code, 'id')) as ProductCode[],
        },
        helpers,
      )
    },
    [onSubmit],
  )

  return (
    <Formik onSubmit={handleOnSubmit} initialValues={initialValues} validationSchema={validationSchema}>
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      {(formikProps: FormikProps<Product>) =>
        formikProps.values.type === 'bundle' || formikProps.values.type === 'virtual' ? (
          <ProductSetForm {...formikProps} {...rest} setType={setType} hasCodes={!!initialValues?.codes?.length} />
        ) : (
          <ProductEditForm
            {...formikProps}
            {...rest}
            setType={setType}
            workAroundLot={formikProps.values.workAroundLot}
            hasCodes={!!initialValues?.codes?.length}
          />
        )
      }
    </Formik>
  )
}

export default withAcl(ProductEditFormFormik)
